import React, { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { CalendarClock, UserRound } from "lucide-react";
import Spinner1 from "../common/spinners";
import moment from "moment";
import { useDashboard } from "../../context/dashboardContext";
import { formatCurrency } from "../../utils/utilities";

const statusClassMap = {
  Missed: "is-danger",
  Approved: "is-success",
  Cancelled: "is-neutral",
  Completed: "is-success",
  "Pending Paypoint Approval": "is-warning",
};

const MedicalRecordsTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const { handleFetAppointments, loading, appointments } = useDashboard();

  useEffect(() => {
    if (!appointments?.length) {
      handleFetAppointments();
    }
  }, []);

  const filteredData = useMemo(
    () =>
      (Array.isArray(appointments) ? appointments : []).filter(
        (item) =>
          item?.appointmentType?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.status?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.whomToSee?.first_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.department?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [appointments, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <>
      <div id="visits" className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Visit history</h5>
          <p>Review your previous consultations, clinicians, charges, and visit status from one cleaner history table.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search doctor, appointment type, or status"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No visit history found</h6>
            <p>Your previous visits will appear here once there are consultation records to show.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Doctor</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((appointment) => (
                  <tr key={appointment?.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <UserRound size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title text-capitalize">
                            {appointment?.whomToSee?.first_name ?? "---"} {appointment?.whomToSee?.last_name ?? "---"}
                          </div>
                          <div className="data-cell-meta text-capitalize">
                            {appointment?.whomToSee?.profession ?? "---"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title text-capitalize">
                        {appointment?.consultation_type ?? "---"}
                      </div>
                      <div className="data-cell-meta text-capitalize">
                        {appointment?.department?.name ?? "---"}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <CalendarClock size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title">{formatCurrency(appointment?.service?.tariff)}</div>
                          <div className="data-cell-meta">
                            {moment(appointment?.appointment_date).format("dddd, Do MMM YYYY")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${statusClassMap[appointment?.status] || "is-neutral"}`}>
                        {appointment?.status || "---"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default MedicalRecordsTab;
